import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// The meeting page's minutes table (Kloepfer K3-36/K3-37, 2026-10-19).
// - The kind chips filter the rows; "Actions" switches the table to the
//   to-do columns, laid out like Keane's ChatGPT list (data-view="todo").
// - "List:" sizes it like the Submittal Log: Full list / Important /
//   Essentials. A view only: hidden minutes stay saved. The choice is kept
//   on this computer.
// - On the Actions tab, ticked suggestions become Action Items in one go;
//   their rows update in place (no reload).
const SIZES = {
  full: ["essential", "important", "detail"],
  important: ["essential", "important"],
  essentials: ["essential"]
}
const SIZE_KEY = "conslog.minutesListSize"

export default class extends Controller {
  static targets = ["chip", "table", "size", "pick", "pickAll", "selectionBar", "pickedCount",
    "makeButton", "makeLabel", "convertAll", "convertAllLabel", "nudge"]
  static values = { convertUrl: String, projectId: Number, meetingId: Number, words: Object }

  connect() {
    this._kind = "all"
    this._size = "full"
    if (this.hasSizeTarget) {
      try {
        const saved = window.localStorage.getItem(SIZE_KEY)
        if (saved && SIZES[saved]) this._size = saved
      } catch { /* storage blocked: keep Full list */ }
    }
    this._apply()
  }

  filter(event) {
    this._kind = event.currentTarget.dataset.kind || "all"
    this._apply()
  }

  setSize(event) {
    const size = event.currentTarget.dataset.size
    if (!SIZES[size] || size === this._size) return
    this._size = size
    try { window.localStorage.setItem(SIZE_KEY, size) } catch { /* not remembered */ }
    this._apply()
    track("Meeting Minutes List Size Changed", {
      list_size: size, kind: this._kind, items_shown: this._shownRows().length,
      project_id: this.projectIdValue, meeting_id: this.meetingIdValue
    })
  }

  picked() {
    this._paintSelection()
  }

  // A minute's notes show two lines; a click shows them all (and back).
  toggleNote(event) {
    event.currentTarget.classList.toggle("line-clamp-2")
  }

  pickAll(event) {
    const on = event.currentTarget.checked
    this._shownPicks().forEach((p) => { p.checked = on })
    this._paintSelection()
  }

  clearPicks() {
    this.pickTargets.forEach((p) => { p.checked = false })
    this._paintSelection()
  }

  async makeActionItems() {
    const ids = this._shownPicks().filter((p) => p.checked).map((p) => p.dataset.itemId)
    if (!ids.length || this._busy) return

    this._busy = true
    this.makeButtonTarget.disabled = true
    try {
      const res = await fetch(this.convertUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ item_ids: ids }),
        credentials: "same-origin"
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        showToast(data.error || this._word("failed"), "error")
        return
      }
      ;(data.converted || []).forEach((c) => this._markConverted(c))
      this._refreshConvertAll(data)
      track("Meeting Minutes Action Items Made", {
        count: (data.converted || []).length, project_id: this.projectIdValue, meeting_id: this.meetingIdValue
      })
      showToast(data.message)
    } catch {
      showToast(this._word("failed"), "error")
    } finally {
      this._busy = false
      this.makeButtonTarget.disabled = false
      this._paintSelection()
    }
  }

  // ── private ───────────────────────────────────────────────────────────

  _apply() {
    if (!this.hasTableTarget) return

    const todo = this._kind === "action"
    this.tableTarget.dataset.view = todo ? "todo" : "all"
    const allowed = SIZES[this._size]
    const rows = this._rows()
    const kindOf = (r) => r.dataset.kind
    rows.forEach((r) => {
      const show = (this._kind === "all" || kindOf(r) === this._kind) && allowed.includes(r.dataset.importance)
      r.hidden = !show
    })

    this.chipTargets.forEach((chip) => {
      const k = chip.dataset.kind
      const active = k === this._kind
      chip.classList.toggle("bg-brand", active)
      chip.classList.toggle("text-white", active)
      chip.classList.toggle("bg-gray-100", !active)
      chip.classList.toggle("text-gray-600", !active)
      const count = chip.querySelector("[data-count]")
      if (count) count.textContent = rows.filter((r) => allowed.includes(r.dataset.importance) && (k === "all" || kindOf(r) === k)).length
    })

    this.sizeTargets.forEach((btn) => {
      const size = btn.dataset.size
      const on = size === this._size
      btn.classList.toggle("bg-brand/10", on)
      btn.classList.toggle("text-brand", on)
      btn.classList.toggle("font-semibold", on)
      btn.classList.toggle("text-gray-600", !on)
      const count = btn.querySelector("[data-count]")
      if (count) {
        count.textContent = rows.filter((r) => SIZES[size].includes(r.dataset.importance) && (this._kind === "all" || kindOf(r) === this._kind)).length
        count.classList.toggle("text-gray-400", !on)
      }
    })

    if (!todo) this.pickTargets.forEach((p) => { p.checked = false })
    this._paintSelection()
  }

  _rows() {
    return Array.from(this.tableTarget.querySelectorAll(":scope > tbody[data-minute-row]"))
  }

  _shownRows() {
    return this._rows().filter((r) => !r.hidden)
  }

  _shownPicks() {
    return this.pickTargets.filter((p) => !p.closest("tbody")?.hidden)
  }

  _paintSelection() {
    const shown = this._shownPicks()
    const n = shown.filter((p) => p.checked).length
    if (this.hasPickAllTarget) {
      this.pickAllTarget.checked = shown.length > 0 && n === shown.length
      this.pickAllTarget.indeterminate = n > 0 && n < shown.length
    }
    if (!this.hasSelectionBarTarget) return

    const todo = this.hasTableTarget && this.tableTarget.dataset.view === "todo"
    this.selectionBarTarget.hidden = !(todo && n > 0)
    if (n > 0) {
      this.pickedCountTarget.textContent = this._word("ticked").replace("%{count}", n)
      this.makeLabelTarget.textContent = this._word(n === 1 ? "make_one" : "make_many").replace("%{count}", n)
    }
  }

  // A row the server just made into an Action Item: new status and owner,
  // no tick box, no "Make Action Item" in its menu.
  _markConverted({ id, status_html, owner_html }) {
    const row = this.tableTarget.querySelector(`:scope > tbody[data-item-id="${id}"]`)
    if (row) {
      row.dataset.convertible = "false"
      row.querySelector('[data-minutes-filter-target="pick"]')?.remove()
      row.querySelector(`[data-meeting-ui-id-param="convert-item-${id}"]`)?.remove()
      row.querySelector(`#convert-item-${id}`)?.remove()
    }
    this.element.querySelectorAll(`[data-status-cell="${id}"]`).forEach((el) => { el.outerHTML = status_html })
    this.element.querySelectorAll(`[data-owner-chip="${id}"]`).forEach((el) => { el.outerHTML = owner_html })
  }

  // The header's "Convert N" button and the amber nudge follow what's left.
  _refreshConvertAll({ left, convert_all_label: label, nudge_html: nudge }) {
    if (this.hasConvertAllTarget) {
      if (left > 1 && label) this.convertAllLabelTarget.textContent = label
      else this.convertAllTarget.hidden = true
    }
    if (this.hasNudgeTarget) {
      if (nudge) this.nudgeTarget.outerHTML = nudge
      else this.nudgeTarget.remove()
    }
  }

  _word(key) {
    return (this.wordsValue && this.wordsValue[key]) || ""
  }
}
